<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="modelValue"
        class="modal-overlay"
        :class="overlayClass"
        @click.self="handleOverlayClick"
      >
        <div
          class="modal-content relative"
          :class="[
            { 'modal-content--wide': wide, 'modal-content--no-padding': noPadding },
            contentClass
          ]"
          role="dialog"
          :aria-labelledby="title ? titleId : undefined"
          aria-modal="true"
        >
          <!-- Header -->
          <div v-if="title || $slots.header" class="modal-header" :class="headerClass">
            <div class="modal-header-row">
              <slot name="header">
                <h3 v-if="title" :id="titleId" :class="titleClass">{{ title }}</h3>
              </slot>
              <button
                v-if="showCloseButton"
                type="button"
                class="[ modal-close ] modal-close-btn"
                aria-label="Close"
                @click="handleCancel"
              >
                <svg class="h-4 w-4">
                  <use xlink:href="#cross" />
                </svg>
              </button>
            </div>
          </div>

          <button
            v-else-if="showCloseButton"
            type="button"
            class="[ modal-close ] absolute right-4 top-4 z-10 modal-close-btn"
            aria-label="Close"
            @click="handleCancel"
          >
            <svg class="h-4 w-4">
              <use xlink:href="#cross" />
            </svg>
          </button>

          <!-- Body -->
          <div class="modal-body" :class="bodyClass">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div
            v-if="$slots.footer || showDefaultFooter"
            class="modal-footer"
            :class="[{ 'modal-footer--split': splitFooter }, footerClass]"
          >
            <slot name="footer">
              <!-- Default footer with Cancel/Confirm buttons -->
              <button
                class="btn btn-secondary"
                @click="handleCancel"
              >
                {{ cancelText }}
              </button>
              <button
                class="btn"
                :class="confirmDanger ? 'btn-danger' : 'btn-primary'"
                @click="handleConfirm"
              >
                {{ confirmText }}
              </button>
            </slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from 'vue';
import { lockBodyScroll as lockBodyScrollUtil, unlockBodyScroll as unlockBodyScrollUtil } from '@/utils/bodyScrollLock';

defineOptions({ name: 'BaseModal' });

const props = defineProps({
  /** Controls modal visibility (v-model) */
  modelValue: {
    type: Boolean,
    required: true
  },
  /** Modal title displayed in header */
  title: {
    type: String,
    default: ''
  },
  /** Additional CSS class(es) applied to the title element */
  titleClass: {
    type: String,
    default: ''
  },
  /** Additional CSS class(es) applied to modal header wrapper */
  headerClass: {
    type: String,
    default: ''
  },
  /** Use wider modal variant */
  wide: {
    type: Boolean,
    default: false
  },
  /** Remove default modal padding (for custom header/body layouts) */
  noPadding: {
    type: Boolean,
    default: false
  },
  /** Close modal when clicking overlay */
  closeOnOverlay: {
    type: Boolean,
    default: false
  },
  /** Close modal when pressing ESC key */
  closeOnEsc: {
    type: Boolean,
    default: true
  },
  /** Lock body scrolling while modal is open */
  lockBodyScroll: {
    type: Boolean,
    default: true
  },
  /** Show a close (X) button in the header */
  showCloseButton: {
    type: Boolean,
    default: true
  },
  /** Additional CSS class(es) applied to modal content container */
  contentClass: {
    type: String,
    default: ''
  },
  /** Additional CSS class(es) applied to modal overlay */
  overlayClass: {
    type: String,
    default: ''
  },
  /** Additional CSS class(es) applied to modal body wrapper */
  bodyClass: {
    type: String,
    default: ''
  },
  /** Additional CSS class(es) applied to modal footer wrapper */
  footerClass: {
    type: String,
    default: ''
  },
  /** Show default footer with Cancel/Confirm buttons */
  showDefaultFooter: {
    type: Boolean,
    default: false
  },
  /** Text for cancel button (default footer) */
  cancelText: {
    type: String,
    default: 'Cancel'
  },
  /** Text for confirm button (default footer) */
  confirmText: {
    type: String,
    default: 'Confirm'
  },
  /** Use danger styling for confirm button */
  confirmDanger: {
    type: Boolean,
    default: false
  },
  /** Split footer layout (left + right aligned buttons) */
  splitFooter: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'close']);

// Generate unique ID for aria-labelledby
const titleId = computed(() => `modal-title-${Math.random().toString(36).slice(2, 9)}`);

let escListenerAttached = false;
let bodyScrollLocked = false;

function close () {
  emit('update:modelValue', false);
  emit('close');
}

function handleOverlayClick () {
  if (props.closeOnOverlay) {
    close();
    emit('cancel');
  }
}

function handleCancel () {
  close();
  emit('cancel');
}

function handleConfirm () {
  emit('confirm');
  // Note: caller should close modal after handling confirm if needed
}

function handleEscKey (e) {
  if (e.key === 'Escape' && props.modelValue && props.closeOnEsc) {
    close();
    emit('cancel');
  }
}

function attachEscListener () {
  if (escListenerAttached) return;
  document.addEventListener('keydown', handleEscKey);
  escListenerAttached = true;
}

function detachEscListener () {
  if (!escListenerAttached) return;
  document.removeEventListener('keydown', handleEscKey);
  escListenerAttached = false;
}

function lockBodyScrollIfNeeded () {
  if (bodyScrollLocked) return;
  lockBodyScrollUtil();
  bodyScrollLocked = true;
}

function unlockBodyScrollIfNeeded () {
  if (!bodyScrollLocked) return;
  unlockBodyScrollUtil();
  bodyScrollLocked = false;
}

watch(
  () => [props.modelValue, props.closeOnEsc],
  ([isOpen, allowEsc]) => {
    if (isOpen && allowEsc) attachEscListener();
    else detachEscListener();
  },
  { immediate: true }
);

watch(
  () => [props.modelValue, props.lockBodyScroll],
  ([isOpen, lock]) => {
    if (isOpen && lock) lockBodyScrollIfNeeded();
    else unlockBodyScrollIfNeeded();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  detachEscListener();
  unlockBodyScrollIfNeeded();
});
</script>

<style scoped>
/*
 * Component uses global modal styles from src/css/modal.css
 * No additional scoped styles needed
 */
</style>
